import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, Check, Star, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { YARBOCARE_PLANS, YARBOCARE_DISCLAIMER, YARBOCARE_PROVIDER_NOTE, YarboCarePlanId } from '../data/yarboCare';
/**
 * Post-add-to-cart YarboCare upsell modal.
 *
 * Developer handoff note: this is a design mockup. Final eligibility,
 * pricing, and plan selection should be wired to the warranty provider.
 * The modal does NOT preselect a paid plan and always exposes
 * "Continue Without Protection".
 */
export function YarboCareModal() {
  const {
    yarboCareModalOpen,
    yarboCareTargetProductId,
    items,
    attachProtection,
    closeYarboCareModal
  } = useCart();
  const item = items.find(i => i.product.id === yarboCareTargetProductId);
  const [selected, setSelected] = useState<YarboCarePlanId | null>(null);
  // Reset selection whenever a new product triggers the modal
  useEffect(() => {
    if (yarboCareModalOpen) setSelected(null);
  }, [yarboCareModalOpen, yarboCareTargetProductId]);
  return <AnimatePresence data-id="element-246">
      {yarboCareModalOpen && item && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={closeYarboCareModal} data-id="element-247">
          <motion.div initial={{
        y: 30,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 30,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 280
      }} onClick={e => e.stopPropagation()} className="relative bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col" role="dialog" aria-labelledby="yarbocare-title" data-id="element-248">
            {/* Header */}
            <div className="flex items-start justify-between p-5 sm:p-6 border-b border-gray-100" data-id="element-249">
              <div className="flex items-start gap-3" data-id="element-250">
                <div className="w-10 h-10 rounded-full bg-yarbo-yellow flex items-center justify-center flex-shrink-0" data-id="element-251">
                  <ShieldCheck className="w-5 h-5 text-yarbo-dark" data-id="element-252" />
                </div>
                <div data-id="element-253">
                  <h2 id="yarbocare-title" className="text-lg sm:text-xl font-extrabold text-yarbo-dark leading-tight" data-id="element-254">
                    Add YarboCare Protection?
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 max-w-lg" data-id="element-255">
                    Your Yarbo system includes standard 2-year limited warranty
                    coverage. YarboCare can extend eligible systems with
                    additional warranty coverage and optional accidental damage
                    protection.
                  </p>
                </div>
              </div>
              <button onClick={closeYarboCareModal} className="p-2 -m-2 text-gray-400 hover:text-yarbo-dark" aria-label="Close" data-id="element-256">
                <X className="w-5 h-5" data-id="element-257" />
              </button>
            </div>

            {/* Product summary */}
            <div className="px-5 sm:px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center gap-4" data-id="element-258">
              <div className="w-14 h-14 bg-white rounded-lg p-2 flex-shrink-0 border border-gray-200" data-id="element-259">
                <img src={item.product.image} alt={item.product.name} className="w-full h-full object-contain" data-id="element-260" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-261">
                <div className="text-sm font-bold text-yarbo-dark truncate" data-id="element-262">
                  {item.product.name}
                </div>
                <div className="text-xs text-gray-500" data-id="element-263">
                  ${item.product.price.toLocaleString()}
                </div>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1 bg-yarbo-yellow/20 border border-yarbo-yellow/40 text-yarbo-dark text-[10px] font-bold rounded-full px-2.5 py-1 uppercase tracking-wider" data-id="element-264">
                <Check className="w-3 h-3" data-id="element-265" /> Eligible for YarboCare
              </span>
            </div>

            {/* Plan cards */}
            <div className="overflow-y-auto px-5 sm:px-6 py-5 flex-1" data-id="element-266">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4" data-id="element-267">
                {YARBOCARE_PLANS.map(plan => {
              const isSelected = selected === plan.id;
              return <button key={plan.id} type="button" onClick={() => setSelected(plan.id)} className={`text-left rounded-xl border-2 p-4 flex flex-col transition-all ${isSelected ? 'border-yarbo-dark bg-yarbo-yellow/5 shadow-md' : plan.highlight ? 'border-yarbo-yellow/70 bg-white hover:border-yarbo-dark' : 'border-gray-200 bg-white hover:border-gray-400'}`} data-id="element-268">
                      <div className="flex items-center justify-between mb-2 gap-2" data-id="element-269">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 ${plan.highlight ? 'bg-yarbo-yellow text-yarbo-dark' : 'bg-gray-100 text-gray-700'}`} data-id="element-270">
                          {plan.highlight && <Star className="w-3 h-3" data-id="element-271" />}
                          {plan.badge}
                        </span>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-yarbo-dark border-yarbo-dark' : 'border-gray-300'}`} data-id="element-272">
                          {isSelected && <Check className="w-3 h-3 text-white" data-id="element-273" />}
                        </div>
                      </div>
                      <h3 className="font-bold text-yarbo-dark text-sm leading-tight mb-2" data-id="element-274">
                        {plan.title}
                      </h3>
                      <div className="text-xl font-extrabold text-yarbo-dark mb-3" data-id="element-275">
                        +${plan.priceMockup}
                      </div>
                      <ul className="space-y-1.5 mb-4 flex-1" data-id="element-276">
                        {plan.bullets.map((b, i) => <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700 leading-relaxed" data-id="element-277">
                            <Check className="w-3 h-3 text-yarbo-green mt-0.5 flex-shrink-0" data-id="element-278" />
                            <span data-id="element-279">{b}</span>
                          </li>)}
                      </ul>
                      <div className={`w-full text-center text-xs font-bold py-2 rounded-lg transition-colors ${isSelected ? 'bg-yarbo-dark text-white' : 'bg-gray-100 text-yarbo-dark'}`} data-id="element-280">
                        {isSelected ? 'Selected' : 'Add This Plan'}
                      </div>
                    </button>;
            })}
              </div>

              <div className="mt-5 flex items-start gap-2 text-[11px] text-gray-500 leading-relaxed" data-id="element-281">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-282" />
                <p data-id="element-283">
                  {YARBOCARE_DISCLAIMER} {YARBOCARE_PROVIDER_NOTE}
                </p>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 p-5 sm:p-6 border-t border-gray-100 bg-gray-50" data-id="element-284">
              <button onClick={closeYarboCareModal} className="text-sm font-bold text-gray-700 hover:text-yarbo-dark underline underline-offset-4 text-center sm:text-left" data-id="element-285">
                Continue Without Protection
              </button>
              <button onClick={() => {
            if (selected && yarboCareTargetProductId) {
              attachProtection(yarboCareTargetProductId, selected);
            }
            closeYarboCareModal();
          }} disabled={!selected} className={`inline-flex items-center justify-center font-bold px-6 py-3.5 rounded-lg transition-colors text-sm ${selected ? 'bg-yarbo-dark text-white hover:bg-black' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-286">
                <ShieldCheck className="w-4 h-4 mr-2" data-id="element-287" /> Add Protection &amp;
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}